import { Loader, Pill } from '@recruit-robin/robin-theme/web-components';
import { FileAttachment } from '@recruit-robin/robin-theme/web-icons';

import type { Vacancy } from 'clients/AutoCompleteClient/types';

import { useIsMutating } from '@tanstack/react-query';
import { useGTM } from 'hooks/gtm';
import { EnrichTab } from 'shared/legacy/contexts/DialogContext/modals/EnrichVacancyModal/components/EditEnrichedVacancyForm/EditEnrichedVacancy.types';
import { useFeatureToggles } from 'shared/legacy/hooks/useFeatureToggles';
import { GlobalDialogType } from 'stores/global-dialogs/global-dialogs.store.types';
import { useGlobalDialog } from 'stores/global-dialogs/hooks/useGlobalDialog';
import { QueryKey } from 'types/query';
import { useStyles } from './SelectedVacancyPill.styles';

export const SelectedVacancyPill = ({
  selectedVacancy,
  onRemove,
}: {
  selectedVacancy: Vacancy;
  onRemove: () => void;
}) => {
  const classes = useStyles();
  const enrichVacancyModal = useGlobalDialog(GlobalDialogType.EnrichVacancy);
  const gtm = useGTM();

  const { isFeatureToggleEnabled } = useFeatureToggles();

  const isAutoProcessingJobPreview = useIsMutating([QueryKey.useAutoProcessJobPreviewVacancy]);

  const handleOpenEnrichVacancyModal = () => {
    if (isAutoProcessingJobPreview) return;

    gtm.jobPreviewOpened({
      jobImprovedId: selectedVacancy.db_id,
      atsId: selectedVacancy.job_ats_id,
      indeedId: selectedVacancy.job_indeed_id,
    });

    const isIndeedCreatedJob = selectedVacancy.type === 'INDEED' && !!selectedVacancy.originalIndeedJob;

    enrichVacancyModal.open({
      payload: {
        vacancy: selectedVacancy,
        initialEditVacancyTab: isIndeedCreatedJob ? EnrichTab.JobDescription : EnrichTab.JobPreview,
      },
    });
  };

  return (
    <div css={[classes.pill, classes.position]}>
      <Pill
        label={selectedVacancy.name}
        startIcon={isAutoProcessingJobPreview ? <Loader size={16} color={(c) => c.neutral.variant} /> : FileAttachment}
        onClick={
          isFeatureToggleEnabled((f) => f.RJ_COMMUNICATION_HUB) && selectedVacancy.is_improved
            ? handleOpenEnrichVacancyModal
            : undefined
        }
        onEndIconClick={onRemove}
        variant="close"
        style="filter-tag"
        type="subtle1"
        size="small"
        padding="both"
      />
    </div>
  );
};
